'use client';

import { useCallback, useRef, useState } from 'react';
import { Linkedin, Loader2, MapPin, Building2, CheckCircle2, AlertCircle } from 'lucide-react';

import { Button } from '@/components/ui/forms/button';
import { Input } from '@/components/ui/forms/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from '@/components/ui/layout/dialog';
import { DialogHeading, HintNote } from '@/components/ui/design-system';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface LinkedInPreview {
  fullName: string;
  headline: string;
  company: string;
  location: string;
  photoUrl: string | null;
}

export interface LinkedInResumeData {
  firstName: string;
  lastName: string;
  headline: string;
  summary: string;
  employment: Array<{
    id: string;
    title: string;
    company: string;
    location: string;
    startDate: string;
    endDate: string;
    isCurrentlyWorking: boolean;
    responsibilities: string;
  }>;
  education: Array<{
    id: string;
    institution: string;
    degree: string;
    field: string;
    startDate: string;
    endDate: string;
  }>;
  skills: string[];
  location: string;
}

type Phase = 'input' | 'looking-up' | 'confirm' | 'filling' | 'error';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface LinkedInFillDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the full resume data after the user confirms. */
  onFill: (data: LinkedInResumeData) => Promise<void> | void;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

const LINKEDIN_URL_PATTERN = /linkedin\.com\/in\//i;

function isValidLinkedInUrl(url: string): boolean {
  return LINKEDIN_URL_PATTERN.test(url.trim());
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LinkedInFillDialog({
  open,
  onOpenChange,
  onFill,
}: LinkedInFillDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent layout="panel" className="min-[640px]:max-w-lg">
        <LinkedInFillDialogBody
          onOpenChange={onOpenChange}
          onFill={onFill}
        />
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Body (unmounts on dialog close, so state resets automatically)
// ---------------------------------------------------------------------------

function LinkedInFillDialogBody({
  onOpenChange,
  onFill,
}: Omit<LinkedInFillDialogProps, 'open'>) {
  const [url, setUrl] = useState('');
  const [phase, setPhase] = useState<Phase>('input');
  const [preview, setPreview] = useState<LinkedInPreview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  // -------------------------------------------------------------------------
  // Lookup
  // -------------------------------------------------------------------------

  const handleLookup = useCallback(async () => {
    const trimmed = url.trim();
    if (!isValidLinkedInUrl(trimmed)) {
      setError('Please enter a valid LinkedIn profile URL (e.g. linkedin.com/in/username)');
      setPhase('error');
      return;
    }

    setPhase('looking-up');
    setError(null);
    setPreview(null);

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const response = await fetch('/api/resume-builder/linkedin-lookup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ linkedinUrl: trimmed }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const message =
          response.status === 404
            ? 'No profile found for this LinkedIn URL. Check the URL and try again.'
            : response.status === 429
              ? 'Too many lookups. Please wait a moment and try again.'
              : (data as { error?: string }).error || 'Something went wrong. Please try again.';
        setError(message);
        setPhase('error');
        return;
      }

      const data = (await response.json()) as {
        found: boolean;
        profile: LinkedInPreview;
      };

      if (!data.found || !data.profile) {
        setError('No profile found for this LinkedIn URL.');
        setPhase('error');
        return;
      }

      setPreview(data.profile);
      setPhase('confirm');
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      console.error('[LinkedInFillDialog] lookup error:', err);
      setError('Failed to look up profile. Please try again.');
      setPhase('error');
    }
  }, [url]);

  // -------------------------------------------------------------------------
  // Confirm and fill
  // -------------------------------------------------------------------------

  const handleConfirmFill = useCallback(async () => {
    const trimmed = url.trim();
    setPhase('filling');
    setError(null);

    try {
      const response = await fetch('/api/resume-builder/linkedin-fill', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ linkedinUrl: trimmed }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setError((data as { error?: string }).error || 'Failed to retrieve profile data.');
        setPhase('error');
        return;
      }

      const data = (await response.json()) as {
        success: boolean;
        data: LinkedInResumeData;
      };

      await onFill(data.data);
      onOpenChange(false);
    } catch (err) {
      console.error('[LinkedInFillDialog] fill error:', err);
      setError('Failed to fill resume from LinkedIn. Please try again.');
      setPhase('error');
    }
  }, [onFill, onOpenChange, url]);

  // -------------------------------------------------------------------------
  // Reset to try a different URL
  // -------------------------------------------------------------------------

  const handleTryDifferent = useCallback(() => {
    setPhase('input');
    setPreview(null);
    setError(null);
    setUrl('');
  }, []);

  // -------------------------------------------------------------------------
  // Computed
  // -------------------------------------------------------------------------

  const isBusy = phase === 'looking-up' || phase === 'filling';

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <>
      <DialogHeading
        leading={
          <div className="flex size-9 items-center justify-center border border-border/60 bg-muted/30 text-primary">
            <Linkedin className="size-4" />
          </div>
        }
        title="Fill from LinkedIn"
        description="Paste your LinkedIn profile URL. We'll look up your professional details and fill in the resume for you."
      />

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="space-y-4 px-4 py-4 min-[640px]:px-6">
          {/* URL input */}
          {(phase === 'input' || phase === 'looking-up' || phase === 'error') && !preview && (
            <div className="space-y-3">
              <div className="space-y-1">
                <p className="text-label text-foreground">LinkedIn profile URL</p>
                <p className="text-caption text-muted-foreground">
                  Paste the URL from your browser when viewing your LinkedIn profile.
                </p>
              </div>
              <div className="flex gap-2">
                <Input
                  value={url}
                  onChange={(e) => {
                    setUrl(e.target.value);
                    if (error) setError(null);
                    if (phase === 'error') setPhase('input');
                  }}
                  placeholder="https://linkedin.com/in/your-profile"
                  disabled={isBusy}
                  className="flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !isBusy) {
                      e.preventDefault();
                      void handleLookup();
                    }
                  }}
                />
                <Button
                  onClick={() => void handleLookup()}
                  disabled={isBusy || !url.trim()}
                  className="shrink-0 border"
                  style={{ boxShadow: 'var(--shadow-warm-sm)' }}
                >
                  {phase === 'looking-up' ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  Look up
                </Button>
              </div>
            </div>
          )}

          {/* Confirmation card */}
          {preview && (phase === 'confirm' || phase === 'filling') && (
            <div className="space-y-4">
              <div className="space-y-1">
                <p className="text-label text-foreground">Is this your profile?</p>
                <p className="text-caption text-muted-foreground">
                  Confirm this is you, and we'll fill your resume with the details below.
                </p>
              </div>

              <div className="rounded-md border border-border bg-card p-4">
                <div className="flex items-start gap-3">
                  {/* Avatar placeholder */}
                  <div className="flex size-12 shrink-0 items-center justify-center rounded-full border border-border bg-muted/50 text-muted-foreground">
                    <Linkedin className="size-5" />
                  </div>

                  <div className="min-w-0 flex-1 space-y-1">
                    <p className="text-label text-foreground font-medium">
                      {preview.fullName || 'Unknown'}
                    </p>
                    {preview.headline && (
                      <p className="text-caption text-muted-foreground line-clamp-2">
                        {preview.headline}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 pt-0.5">
                      {preview.company && (
                        <span className="inline-flex items-center gap-1 text-caption text-muted-foreground">
                          <Building2 className="size-3" />
                          {preview.company}
                        </span>
                      )}
                      {preview.location && (
                        <span className="inline-flex items-center gap-1 text-caption text-muted-foreground">
                          <MapPin className="size-3" />
                          {preview.location}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <DialogFooter className="shrink-0 border-t border-border bg-background px-4 py-4 min-[640px]:px-6">
        <div className="flex w-full flex-col gap-3">
          {error && (
            <HintNote className="border-destructive/40 bg-destructive/10 text-destructive">
              <AlertCircle className="mr-1.5 inline-block size-3.5 shrink-0" />
              {error}
            </HintNote>
          )}

          <div className="flex justify-end gap-3">
            {/* "Try different URL" when confirming */}
            {preview && phase === 'confirm' && (
              <Button
                variant="outline"
                onClick={handleTryDifferent}
                className="border"
                style={{ boxShadow: 'var(--shadow-warm-sm)' }}
              >
                Not me
              </Button>
            )}

            {/* Cancel on input / error */}
            {(!preview || phase === 'error') && (
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="border"
                style={{ boxShadow: 'var(--shadow-warm-sm)' }}
              >
                Cancel
              </Button>
            )}

            {/* Confirm fill */}
            {preview && (phase === 'confirm' || phase === 'filling') && (
              <Button
                onClick={() => void handleConfirmFill()}
                disabled={phase === 'filling'}
                className="border"
                style={{ boxShadow: 'var(--shadow-warm-sm)' }}
              >
                {phase === 'filling' ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                )}
                {phase === 'filling' ? 'Filling resume...' : "Yes, fill my resume"}
              </Button>
            )}
          </div>
        </div>
      </DialogFooter>
    </>
  );
}
